// Answers as WarmHub-shaped graphs: existing Things by pinned wref, plus new
// collections, assertions and local Things as local nodes. See Design >
// Answer shapes.

import { Schema } from "effect";
import { MAX_COLLECTION_MEMBERS } from "./limits.ts";
import { Json, JsonObject } from "./json.ts";
import type { ShapeDef } from "./defs.ts";
import { checkShapeDef, isShapeName, parseShapeRef, shapeKeyFromRef } from "./schema.ts";

/** A local node of an answer, such as "_:n1", which AnswerBuilder makes. */
export type LocalId = `_:${string}`;
/** A node of an answer: a LocalId, or the pinned wref of a Thing the answer includes. */
export type NodeRef = string;

const NodeRefs = Schema.mutable(Schema.Array(Schema.String));
/**
 * Decodes an AnswerNode read from outside, such as a tool's outcome that
 * crossed RPC from its tool sandbox. Decoding checks the graph's form;
 * validateAnswer checks it against the intent and the shape definitions.
 */
export const AnswerNode = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("collection"), type: Schema.Literal("Set"), members: NodeRefs }),
  Schema.Struct({ kind: Schema.Literal("collection"), type: Schema.Literal("List"), items: NodeRefs }),
  Schema.Struct({ kind: Schema.Literal("collection"), type: Schema.Literal("Arc"), from: Schema.String, to: Schema.String }),
  Schema.Struct({ kind: Schema.Literal("collection"), type: Schema.Literal("Bond"), ends: Schema.mutable(Schema.Tuple([Schema.String, Schema.String])) }),
  Schema.Struct({ kind: Schema.Literal("assertion"), shape: Schema.String, about: Schema.String, data: JsonObject }),
  Schema.Struct({ kind: Schema.Literal("thing"), shape: Schema.String, data: JsonObject }),
]);
/**
 * A local node of an answer: a collection (Set, List, Arc or Bond) of other
 * nodes, each a NodeRef; an assertion with its shape, the node it's about,
 * and its data; or a local Thing, which exists only in the answer, with its
 * shape and data. See Design > Answer shapes.
 */
export type AnswerNode = typeof AnswerNode.Type;

/** Decodes a ThingSnapshot read from outside, as AnswerNode does; its data must be JSON. */
export const ThingSnapshot = Schema.Struct({
  /** Shape ref, such as "wh:warmhub-data/us.geography/Shape/Place@v2". */
  shape: Schema.String,
  /** The Thing's current name, for display. */
  name: Schema.String,
  durableId: Schema.optionalKey(Schema.String),
  data: Json,
});
/** An existing Thing as an answer includes it. Build one with snapshot(). */
export type ThingSnapshot = typeof ThingSnapshot.Type;

/**
 * Decodes an answer graph read from outside, such as a tool's outcome that
 * crossed RPC from its tool sandbox. Decoding checks the graph's form;
 * validateAnswer checks it against the intent and the shape definitions.
 */
export const ShapedAnswer = Schema.Struct({
  root: Schema.String,
  /** The local nodes, by LocalId. */
  nodes: Schema.Record(Schema.String, AnswerNode),
  /** The existing Things, by pinned wref. */
  things: Schema.Record(Schema.String, ThingSnapshot),
});
/**
 * An answer as a WarmHub-shaped graph: the root node, the local nodes by
 * LocalId, and the existing Things by pinned wref. Build it with
 * AnswerBuilder; the HubRouter checks it with validateAnswer before the
 * consumer sees it.
 */
export type ShapedAnswer = typeof ShapedAnswer.Type;

const isLocal = (ref: string): ref is LocalId => ref.startsWith("_:");

/**
 * Builds an answer graph. Tools call thing() for each existing Thing they
 * include and localThing() for each one that exists only in the answer, then
 * add collections and assertions, then build() with the root.
 * Each adding method returns the node's reference, to pass to the next. Use
 * a new builder for each call of the tool.
 */
export class AnswerBuilder {
  private nodes: Record<string, AnswerNode> = {};
  private things: Record<string, ThingSnapshot> = {};
  private next = 1;

  /**
   * Includes an existing Thing, under its pinned wref (a ThingRecord's
   * `pinned`), and returns that wref as its node reference. An unpinned wref
   * breaks rule 7.
   */
  thing(wref: string, snapshot: ThingSnapshot): string {
    this.things[wref] = snapshot;
    return wref;
  }

  private add(node: AnswerNode, id?: string): LocalId {
    const local: LocalId = id ? `_:${id}` : `_:n${this.next++}`;
    this.nodes[local] = node;
    return local;
  }

  /**
   * Adds an unordered collection. `id` names the node "_:<id>"; without it,
   * the builder numbers it. Reusing an `id` replaces that node.
   */
  set(members: NodeRef[], id?: string): LocalId {
    return this.add({ kind: "collection", type: "Set", members }, id);
  }

  /** Adds an ordered collection. `id` works as in set(). */
  list(items: NodeRef[], id?: string): LocalId {
    return this.add({ kind: "collection", type: "List", items }, id);
  }

  /** Adds a directed pair, from one node to another. `id` works as in set(). */
  arc(from: NodeRef, to: NodeRef, id?: string): LocalId {
    return this.add({ kind: "collection", type: "Arc", from, to }, id);
  }

  /** Adds an undirected pair. `id` works as in set(). */
  bond(a: NodeRef, b: NodeRef, id?: string): LocalId {
    return this.add({ kind: "collection", type: "Bond", ends: [a, b] }, id);
  }

  /**
   * Adds a local Thing: one that exists only in the answer, such as a record
   * from an outside API, with no WarmHub wref. `shape` is a pinned shape ref
   * in one of the hub's readable repositories or a manifest shape's name, and
   * its data must decode with that shape's definition (rule 5). Assertions
   * can be about it. `id` works as in set(): name it, such as "apple" for
   * "_:apple", since a consumer shows a local Thing by its name; one the
   * builder numbers shows by its shape's name.
   */
  localThing(shape: string, data: JsonObject, id?: string): LocalId {
    return this.add({ kind: "thing", shape, data }, id);
  }

  /**
   * Adds an assertion of `shape`, a pinned shape ref in one of the hub's
   * readable repositories or a manifest shape's name, about a node. Its data
   * must decode with that shape's definition (rule 5), and every wref in it
   * must be pinned (rule 7). `id` works as in set().
   */
  assertion(shape: string, about: NodeRef, data: JsonObject, id?: string): LocalId {
    return this.add({ kind: "assertion", shape, about, data }, id);
  }

  /**
   * The answer, with `root` as its root, whose shape must be one of the
   * intent's rootShapes (rule 3). It doesn't validate the answer: run
   * validateAnswer from testing.ts in the tool's tests.
   */
  build(root: NodeRef): ShapedAnswer {
    return { root, nodes: this.nodes, things: this.things };
  }
}

function refsOf(node: AnswerNode): NodeRef[] {
  switch (node.kind) {
    case "thing":
      return [];
    case "assertion":
      return [node.about];
    case "collection":
      switch (node.type) {
        case "Set": return node.members;
        case "List": return node.items;
        case "Arc": return [node.from, node.to];
        case "Bond": return [...node.ends];
      }
  }
}

/**
 * The shape of a node: an assertion's or a local Thing's shape, a
 * collection's system shape, such as "system:Set", or an existing Thing's
 * shape ref. Undefined for a reference the answer doesn't hold.
 */
export function nodeShape(answer: ShapedAnswer, ref: NodeRef): string | undefined {
  if (isLocal(ref)) {
    const node = answer.nodes[ref];
    if (!node) return undefined;
    return node.kind === "collection" ? `system:${node.type}` : node.shape;
  }
  return answer.things[ref]?.shape;
}

/**
 * The shapes an intent allows in its answers, from its manifest's `answer`.
 * Shapes are matched by "org/repo/Name", "system:Set" or a manifest shape's
 * name, without version: a Thing can conform to an earlier version of its
 * shape than the current one.
 */
export type AnswerExpectation = {
  /** Allowed shapes for the root (rule 3). */
  rootShapes: readonly string[];
  /** Allowed shapes for collection members (rule 6). Without it, any shape is allowed. */
  memberShapes?: readonly string[];
};

function shapeKeyOf(shape: string | undefined): string | undefined {
  if (!shape) return undefined;
  return shape.startsWith("system:") || isShapeName(shape) ? shape : shapeKeyFromRef(shape);
}

const PINNED = /@v\d+$/;
const WREF_LIKE = /^(wh:|[a-z][a-z0-9-]*\/)/i;

/**
 * The shapes of an answer's assertions and local Things, once each, whose
 * definitions rule 5 needs: pinned shape refs and manifest shapes' names.
 * The HubRouter reads each pinned ref from WarmHub and takes each name from
 * the manifest; in a tool's tests, build each with shapeDefFromWarmHub, or a
 * manifest shape's with manifestShapeDefOf, to pass validateAnswer.
 */
export function shapesToCheck(answer: ShapedAnswer): string[] {
  return [...new Set(Object.values(answer.nodes).flatMap((n) => (n.kind === "collection" ? [] : [n.shape])))];
}

/**
 * Checks the rules in Design > Answer shapes, "Rules", as the HubRouter does
 * before an answer reaches the consumer; a broken rule there is a HubError at
 * stage ToolContract. Each message starts "Rule <n>:", numbered as the page
 * numbers the rules. Rule 4, that a shape is in the hub's readable
 * repositories, isn't here: the HubRouter checks it before it reads any
 * definition. `defs` holds the definition of each shape shapesToCheck names,
 * by pinned ref or manifest shape name; an assertion or local Thing whose
 * shape has none breaks rule 5. An empty list means the answer is valid. It
 * never throws.
 */
export function validateAnswer(answer: ShapedAnswer, expect: AnswerExpectation, defs: Readonly<Record<string, Pick<ShapeDef, "fields">>>): string[] {
  const problems: string[] = [];
  const resolves = (ref: string) => (isLocal(ref) ? ref in answer.nodes : ref in answer.things);

  // Rule 1: every reference resolves.
  if (!resolves(answer.root)) problems.push(`Rule 1: root ${answer.root} doesn't resolve`);
  for (const [id, node] of Object.entries(answer.nodes)) {
    for (const ref of refsOf(node)) if (!resolves(ref)) problems.push(`Rule 1: ${id} points to ${ref}, which doesn't resolve`);
  }

  // Rule 2: no cycles among local nodes.
  const state = new Map<string, "visiting" | "done">();
  const visit = (id: string): boolean => {
    if (state.get(id) === "done") return true;
    if (state.get(id) === "visiting") return false;
    state.set(id, "visiting");
    const node = answer.nodes[id];
    for (const ref of node ? refsOf(node) : []) if (isLocal(ref) && !visit(ref)) return false;
    state.set(id, "done");
    return true;
  };
  for (const id of Object.keys(answer.nodes)) if (!visit(id)) { problems.push(`Rule 2: cycle through ${id}`); break; }

  // Rule 3: the root has an expected shape.
  const rootShape = shapeKeyOf(nodeShape(answer, answer.root));
  if (!rootShape || !expect.rootShapes.includes(rootShape)) {
    problems.push(`Rule 3: root shape ${rootShape ?? "unknown"} isn't one of ${expect.rootShapes.join(", ")}`);
  }

  for (const [id, node] of Object.entries(answer.nodes)) {
    if (node.kind !== "collection") {
      // Rule 5: an assertion's or a local Thing's data decodes with its shape's definition.
      // Only the record's own entries: a name such as "constructor" isn't a definition.
      const def = Object.hasOwn(defs, node.shape) ? defs[node.shape] : undefined;
      const issues = def ? checkShapeDef(def, node.data) : [`no definition for shape ${node.shape}`];
      for (const issue of issues) problems.push(`Rule 5: ${id}: ${issue}`);
      // Rule 7: wrefs inside its data are pinned.
      for (const [field, value] of Object.entries(node.data)) {
        const values = Array.isArray(value) ? value : [value];
        for (const v of values) {
          if (typeof v === "string" && WREF_LIKE.test(v) && v.includes("/") && !PINNED.test(v)) {
            problems.push(`Rule 7: ${id}.${field} has an unpinned wref ${v}`);
          }
        }
      }
    } else if (node.type === "Set" || node.type === "List") {
      const members = node.type === "Set" ? node.members : node.items;
      // Rule 8: collection size.
      if (members.length > MAX_COLLECTION_MEMBERS) problems.push(`Rule 8: ${id} has ${members.length} members`);
      // Rule 6: member shapes.
      if (expect.memberShapes) {
        for (const m of members) {
          const s = shapeKeyOf(nodeShape(answer, m));
          if (!s || !expect.memberShapes.includes(s)) problems.push(`Rule 6: ${id} member ${m} has shape ${s ?? "unknown"}`);
        }
      }
    }
  }

  // Rule 7 for existing Things: every Thing wref is pinned.
  for (const wref of Object.keys(answer.things)) if (!PINNED.test(wref)) problems.push(`Rule 7: Thing ${wref} isn't pinned`);

  // Rule 9: an entry in `things` is an existing WarmHub Thing, so its shape
  // is a pinned WarmHub shape ref, as snapshot() writes it: never a manifest
  // shape's name or a system shape, which rules 3 and 6 would accept with
  // no check of its data. A Thing that exists only in the answer is a local
  // Thing, whose data rule 5 checks.
  for (const [wref, thing] of Object.entries(answer.things)) {
    if (!parseShapeRef(thing.shape)) problems.push(`Rule 9: Thing ${wref} has the shape ${thing.shape}, which isn't a pinned WarmHub shape ref; one that exists only in the answer is a local Thing`);
  }
  return problems;
}
